import { FunctionComponent, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import OutboxIcon from "@mui/icons-material/Outbox";
import HistoryIcon from "@mui/icons-material/History";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";
import FlightIcon from "@mui/icons-material/Flight";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import { API_URL } from "../../config";
import { useFlightNoteHistory } from "../../hooks/useFlightNoteHistory";

const formatRoute = (departure?: string | null, arrival?: string | null) =>
  departure && arrival ? `${departure} → ${arrival}` : null;

type Props = {
  flight: OpsFlight | null;
  open: boolean;
  onClose: () => void;
};

export const OpsFlightNoteDialog: FunctionComponent<Props> = ({
  flight,
  open,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteType, setNoteType] = useState("");
  const [noteMessage, setNoteMessage] = useState("");
  const [noteError, setNoteError] = useState<string | null>(null);
  const [noteSubmitError, setNoteSubmitError] = useState<string | null>(null);
  const [noteStatus, setNoteStatus] = useState<
    "idle" | "submitting" | "success" | "error"
  >("idle");
  const [targetedDevices, setTargetedDevices] = useState<number | null>(null);
  const [noteStatusSnapshot, setNoteStatusSnapshot] =
    useState<OpsNoteStatus | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  // Fetch note history
  const { data: noteHistory, isLoading: isHistoryLoading, refetch: refetchHistory } = useFlightNoteHistory(
    flight?.flightID || null,
    open
  );

  const route = useMemo(
    () => formatRoute(flight?.departureAirport, flight?.arrivalAirport),
    [flight?.arrivalAirport, flight?.departureAirport]
  );

  const resetNoteForm = () => {
    setNoteTitle("");
    setNoteType("");
    setNoteMessage("");
    setNoteError(null);
    setNoteSubmitError(null);
    setNoteStatus("idle");
    setTargetedDevices(null);
    setNoteStatusSnapshot(null);
    setStatusError(null);
  };

  useEffect(() => {
    if (!open) {
      resetNoteForm();
      setActiveTab(0);
    }
  }, [open, flight?.flightID]);

  useEffect(() => {
    if (open && flight?.flightID) {
      void refreshNoteStatus();
    }
  }, [open, flight?.flightID]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      refetchHistory();
    }
  };

  const refreshNoteStatus = async () => {
    if (!flight?.flightID) {
      return;
    }

    setIsLoadingStatus(true);
    setStatusError(null);

    try {
      const response = await fetch(
        `${API_URL}/ops/notes/${flight.flightID}/status`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error("Unable to load note status");
      }

      const payload = await response.json();
      setNoteStatusSnapshot(payload || null);
    } catch (error) {
      setStatusError(
        error instanceof Error
          ? error.message
          : "Unable to load note status",
      );
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const handleSubmitNote = async () => {

    if (!flight?.flightID) {
      setNoteError("Flight ID is missing for this flight.");
      return;
    }

    const trimmedMessage = noteMessage.trim();
    const trimmedTitle = noteTitle.trim();
    const trimmedType = noteType.trim();

    if (!trimmedMessage) {
      setNoteError("Please enter a message for the note.");
      return;
    }

    setNoteError(null);
    setNoteSubmitError(null);
    setNoteStatus("submitting");

    const requestBody = {
      flightId: flight.flightID,
      flightIdentifier: flight.flightIdentifier,
      title: trimmedTitle || null,
      type: trimmedType || null,
      message: trimmedMessage,
    };

    try {
      const response = await fetch(`${API_URL}/ops/notes`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const retryAfter = response.headers.get("Retry-After");
        if (response.status === 429 && retryAfter) {
          throw new Error(`Rate limited. Try again in ${retryAfter} seconds.`);
        }
        const errorText = await response.text();
        const message = errorText?.trim();
        throw new Error(message || "Unable to send note. Please try again.");
      }

      const payload = await response.json();
      setTargetedDevices(
        Number.isFinite(payload?.targetedDevices)
          ? Number(payload.targetedDevices)
          : null
      );
      setNoteStatus("success");
      void refreshNoteStatus();
    } catch (error) {
      setNoteStatus("error");
      setNoteSubmitError(
        error instanceof Error
          ? error.message
          : "Unable to send note. Please try again.",
      );
    }
  };

  const formatTimestamp = (value?: string | null) => {
    if (!value) {
      return "—";
    }

    const date = new Date(value);
    if (Number.isNaN(date.valueOf())) {
      return "—";
    }

    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderDeliveryBadges = () => {
    if (isLoadingStatus) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading delivery status…
          </Typography>
        </Stack>
      );
    }

    if (!noteStatusSnapshot) {
      return null;
    }

    const queuedCount = noteStatusSnapshot.queuedCount || 0;
    const sentCount = noteStatusSnapshot.sentCount || 0;
    const receivedCount = noteStatusSnapshot.receivedCount || 0;
    const acknowledgedCount = noteStatusSnapshot.acknowledgedCount || 0;
    const pendingCount = noteStatusSnapshot.pendingCount || 0;
    const hasNotes = sentCount > 0 || queuedCount > 0;
    const indicatorColor = !hasNotes
      ? "text.disabled"
      : pendingCount > 0 || queuedCount > 0
      ? "warning.main"
      : "success.main";
    const receivedDisplayCount =
      acknowledgedCount > 0 ? acknowledgedCount : receivedCount;

    return (
      <Stack spacing={1}>
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
        >
          {queuedCount > 0 && (
            <Chip
              color="info"
              size="small"
              icon={<HourglassEmptyIcon fontSize="small" />}
              label={`In queue: ${queuedCount}`}
            />
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: indicatorColor,
              fontWeight: 600,
            }}
          >
            <ArrowUpwardIcon sx={{ fontSize: "1rem" }} />
            <Typography variant="caption" sx={{ color: "inherit", fontWeight: 600 }}>
              {sentCount}
            </Typography>
            <Typography variant="caption" sx={{ color: "inherit" }}>
              Sent
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: indicatorColor,
              fontWeight: 600,
            }}
          >
            <ArrowDownwardIcon sx={{ fontSize: "1rem" }} />
            <Typography variant="caption" sx={{ color: "inherit", fontWeight: 600 }}>
              {receivedDisplayCount}
            </Typography>
            <Typography variant="caption" sx={{ color: "inherit" }}>
              Ack
            </Typography>
          </Box>
          <Chip
            color={pendingCount > 0 ? "warning" : "success"}
            size="small"
            icon={<TaskAltIcon fontSize="small" />}
            label={`Acknowledged: ${acknowledgedCount}`}
          />
          <Chip
            color="primary"
            size="small"
            icon={<OutboxIcon fontSize="small" />}
            label={`Sent to devices: ${sentCount}`}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {`Latest sent: ${formatTimestamp(
            noteStatusSnapshot.latestSentAt
          )} · Latest ack: ${formatTimestamp(
            noteStatusSnapshot.latestAcknowledgedAt
          )}`}
        </Typography>
      </Stack>
    );
  };

  const formatHistoryTimestamp = (value?: string | null) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.valueOf())) return "—";
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderHistoryTab = () => {
    if (isHistoryLoading) {
      return (
        <Stack alignItems="center" justifyContent="center" py={4}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Loading message history...
          </Typography>
        </Stack>
      );
    }

    if (!noteHistory?.notes?.length) {
      return (
        <Stack alignItems="center" justifyContent="center" py={4}>
          <HistoryIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No messages have been sent for this flight yet.
          </Typography>
        </Stack>
      );
    }

    // Helper to determine if a note is queued (not yet sent to devices)
    const isQueued = (note: OpsNoteHistoryEntry) =>
      note.status && note.status !== "sent";

    // Helper to get status display info
    const getStatusInfo = (note: OpsNoteHistoryEntry) => {
      if (note.acknowledgedAt) {
        return { label: "Acknowledged", color: "success" as const };
      }
      if (note.receivedAt) {
        return { label: "Received", color: "info" as const };
      }
      if (isQueued(note)) {
        // Map status to human-readable label
        const statusLabels: Record<string, string> = {
          no_subscribers: "No devices",
          push_disabled: "Push disabled",
          already_sent: "Already sent",
          queued: "In queue",
        };
        return { 
          label: statusLabels[note.status] || "In queue", 
          color: "default" as const 
        };
      }
      return { label: "Pending", color: "warning" as const };
    };

    return (
      <Stack spacing={2}>
        {/* Summary */}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {(noteHistory.summary.totalQueued || 0) > 0 && (
            <Chip
              size="small"
              icon={<HourglassEmptyIcon fontSize="small" />}
              label={`In queue: ${noteHistory.summary.totalQueued}`}
              color="info"
              variant="outlined"
            />
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "primary.main",
              fontWeight: 600,
            }}
          >
            <ArrowUpwardIcon sx={{ fontSize: "0.9rem" }} />
            <Typography variant="caption" sx={{ color: "inherit", fontWeight: 600 }}>
              {noteHistory.summary.totalSent}
            </Typography>
            <Typography variant="caption" sx={{ color: "inherit" }}>
              Sent
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "success.main",
              fontWeight: 600,
            }}
          >
            <ArrowDownwardIcon sx={{ fontSize: "0.9rem" }} />
            <Typography variant="caption" sx={{ color: "inherit", fontWeight: 600 }}>
              {noteHistory.summary.totalAcknowledged}
            </Typography>
            <Typography variant="caption" sx={{ color: "inherit" }}>
              Ack
            </Typography>
          </Box>
          {noteHistory.summary.uniqueSenders.length > 0 && (
            <Chip
              size="small"
              icon={<PersonIcon fontSize="small" />}
              label={`Senders: ${noteHistory.summary.uniqueSenders.length}`}
              variant="outlined"
            />
          )}
          {noteHistory.summary.uniqueReceivers.length > 0 && (
            <Chip
              size="small"
              icon={<FlightIcon fontSize="small" />}
              label={`Receivers: ${noteHistory.summary.uniqueReceivers.join(", ")}`}
              variant="outlined"
            />
          )}
        </Stack>

        <Divider />

        {/* Message List */}
        <List sx={{ maxHeight: 400, overflow: "auto" }}>
          {noteHistory.notes.map((note, index) => {
            const statusInfo = getStatusInfo(note);
            const borderColor = note.acknowledgedAt
              ? "success.main"
              : note.receivedAt
              ? "info.main"
              : isQueued(note)
              ? "grey.400"
              : "warning.main";

            return (
              <ListItem
                key={note.id || index}
                sx={{
                  flexDirection: "column",
                  alignItems: "flex-start",
                  borderLeft: 3,
                  borderColor,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  mb: 1,
                  py: 1.5,
                }}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="subtitle2" fontWeight={600}>
                        {note.title || "Ops Note"}
                      </Typography>
                      {note.noteType && (
                        <Chip size="small" label={note.noteType} sx={{ height: 20, fontSize: "0.7rem" }} />
                      )}
                      <Chip
                        size="small"
                        label={statusInfo.label}
                        color={statusInfo.color}
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    </Stack>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                    >
                      {note.message}
                    </Typography>
                  }
                />
                <Box sx={{ width: "100%", mt: 1.5 }}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <PersonIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary">
                          Sent by: <strong>{note.senderEmail || "Unknown"}</strong>
                          {note.senderRole && ` (${note.senderRole})`}
                        </Typography>
                      </Box>
                      {note.recipientTlc && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <FlightIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                          <Typography variant="caption" color="text.secondary">
                            To: <strong>{note.recipientTlc}</strong>
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                        <Typography variant="caption" color="text.secondary">
                      {note.status && note.status !== "sent"
                        ? `Created: ${formatHistoryTimestamp(note.createdAt)}`
                        : note.deliveredAt
                        ? `Sent: ${formatHistoryTimestamp(note.deliveredAt)}`
                        : `Created: ${formatHistoryTimestamp(note.createdAt)}`}
                        </Typography>
                      </Box>
                      {note.acknowledgedAt && (
                        <Typography variant="caption" color="success.main">
                          ✓ Acknowledged: {formatHistoryTimestamp(note.acknowledgedAt)}
                          {note.receiverTlc && ` by ${note.receiverTlc}`}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </ListItem>
            );
          })}
        </List>
      </Stack>
    );
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" component="span">
            Crew Messages
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {flight?.flightIdentifier || flight?.flightID || ""}
          </Typography>
        </Stack>
      </DialogTitle>
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab icon={<SendIcon fontSize="small" />} iconPosition="start" label="Send Note" />
          <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label="History" />
        </Tabs>
      </Box>
      <DialogContent dividers>
        {activeTab === 0 && (
          <>
            {noteStatus === "success" ? (
              <Stack spacing={2} alignItems="flex-start">
                <CheckCircleOutlineIcon color="success" fontSize="large" />
                <Typography variant="body1" fontWeight={600}>
                  Notification queued
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The note was queued for devices subscribed to this flight.
                  {typeof targetedDevices === "number"
                    ? ` Targeted devices: ${targetedDevices}.`
                    : ""}
                </Typography>
                {renderDeliveryBadges()}
              </Stack>
            ) : (
              <Stack spacing={2}>
                <DialogContentText>
                  Send a short update to the iOS ops clients subscribed to this
                  flight. The message will include the flight identifiers for
                  traceability.
                </DialogContentText>
                <Box>
                  <Typography variant="subtitle2" sx={{ lineHeight: 1.4 }}>
                    {flight?.flightIdentifier || flight?.flightID || "Unknown flight"}
                  </Typography>
                  {route && (
                    <Typography variant="caption" color="text.secondary">
                      {route}
                    </Typography>
                  )}
                </Box>
                <TextField
                  label="Title"
                  fullWidth
                  value={noteTitle}
                  onChange={(event) => setNoteTitle(event.target.value)}
                  placeholder="Ops note"
                />
                <TextField
                  label="Type"
                  fullWidth
                  value={noteType}
                  onChange={(event) => setNoteType(event.target.value)}
                  placeholder="e.g. Diversion, Delay, Crew"
                />
                <TextField
                  label="Message"
                  fullWidth
                  required
                  multiline
                  minRows={4}
                  value={noteMessage}
                  onChange={(event) => {
                    setNoteMessage(event.target.value);
                    if (noteError) {
                      setNoteError(null);
                    }
                  }}
                  error={Boolean(noteError)}
                  helperText={noteError || "Share the note that should be pushed."}
                />
                {statusError && <Alert severity="warning">{statusError}</Alert>}
                {renderDeliveryBadges()}
                {noteSubmitError && (
                  <Alert severity="error">{noteSubmitError}</Alert>
                )}
              </Stack>
            )}
          </>
        )}
        {activeTab === 1 && renderHistoryTab()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {activeTab === 0 && noteStatus !== "success" && (
          <Button
            variant="contained"
            onClick={handleSubmitNote}
            disabled={noteStatus === "submitting" || !noteMessage.trim()}
            startIcon={<SendIcon />}
          >
            {noteStatus === "submitting" ? "Sending" : "Send note"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
